import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { CopyOutlined, SyncOutlined } from '@ant-design/icons';
import { postFile } from '../../helpers/file';
import { useTranslation } from '../../hooks/useTranslation';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import { useFloatingAction } from '../FloatingActions';

interface ClipboardUploadButtonProps {
  currentDir?: string;
  onUploadSuccess?: () => void;
  isMobile?: boolean;
}

const UPLOAD_KEY = 'clipboard_upload_key';

// 根据 MIME 类型获取文件扩展名
const getExtension = (mimeType: string): string => {
  const mimeMap: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
    'application/pdf': 'pdf',
    'application/zip': 'zip',
    'application/x-zip-compressed': 'zip',
    'application/json': 'json',
    'application/xml': 'xml',
    'text/plain': 'txt',
    'text/html': 'html',
    'text/css': 'css',
    'text/javascript': 'js',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
    'audio/mp3': 'mp3',
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',
  };
  return mimeMap[mimeType] || mimeType.split('/')[1] || 'bin';
};

// 根据 MIME 类型获取文件前缀
const getFilePrefix = (mimeType: string): string => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (mimeType.startsWith('text/')) return 'text';
  return 'file';
};

// 判断是否为文本类型
const isTextType = (mimeType: string): boolean => {
  return mimeType.startsWith('text/') ||
         mimeType === 'application/json' ||
         mimeType === 'application/xml';
};

const ClipboardUploadButton: React.FC<ClipboardUploadButtonProps> = ({ 
  currentDir, 
  onUploadSuccess,
  isMobile = false 
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const tRef = useRef(t);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const handleClipboardUpload = useCallback(async () => {
    const translate = tRef.current;

    if (uploading) {
      message.warning(translate('clipboardUpload.uploading'));
      return;
    }

    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        message.error(translate('clipboardUpload.browserNotSupported'));
        return;
      }

      setUploading(true);
      message.loading({ content: translate('clipboardUpload.readingClipboard'), key: UPLOAD_KEY, duration: 0 });

      const clipboardItems = await navigator.clipboard.read();
      
      if (!clipboardItems || clipboardItems.length === 0) {
        message.warning({ content: translate('clipboardUpload.clipboardEmpty'), key: UPLOAD_KEY });
        setUploading(false);
        return;
      }

      let hasFile = false;
      const uploadPromises: Promise<any>[] = [];

      for (const clipboardItem of clipboardItems) {
        for (const type of clipboardItem.types) {
          // 跳过 HTML 类型（通常是复制时的格式信息）
          if (type === 'text/html') continue;
          
          try {
            const blob = await clipboardItem.getType(type);
            
            // 对于纯文本，检查是否为图片 URL
            if (type === 'text/plain') {
              const text = await blob.text();
              const urlPattern = /^https?:\/\/.+\.(jpg|jpeg|png|gif|webp|bmp|svg)/i;
              if (urlPattern.test(text.trim())) {
                message.info({ content: translate('clipboardUpload.imageLinkDetected'), key: UPLOAD_KEY, duration: 3 });
                continue;
              }
            }

            hasFile = true;
            const extension = getExtension(type);
            const prefix = getFilePrefix(type);
            const timestamp = new Date().getTime();
            const fileName = `clipboard_${prefix}_${timestamp}.${extension}`;
            
            let file: File;
            
            // 对于文本类型，使用 UTF-8 编码重新创建文件
            if (isTextType(type)) {
              const text = await blob.text();
              const utf8Blob = new Blob([text], { type: `${type};charset=utf-8` });
              file = new File([utf8Blob], fileName, { type: `${type};charset=utf-8` });
            } else {
              file = new File([blob], fileName, { type });
            }
            
            const uploadPromise = postFile(
              file,
              (percent) => {
                let content = translate('clipboardUpload.uploadProgress', { percent });
                if (percent === 100) {
                  content = translate('clipboardUpload.uploadComplete');
                }
                message.info({ duration: 1000, content, key: UPLOAD_KEY });
              },
              (errorMessage, fileName, traceId, fileSize, httpStatus) => {
                showUploadErrorModal(translate, errorMessage, fileName, traceId, fileSize, httpStatus);
              },
              currentDir
            );
            uploadPromises.push(uploadPromise);
          } catch (e) {
            // 某些类型可能无法读取，跳过
            console.warn(`无法读取类型 ${type}:`, e);
          }
        }
      }

      if (!hasFile) {
        message.warning({ content: translate('clipboardUpload.noContentToUpload'), key: UPLOAD_KEY, duration: 3 });
        setUploading(false);
        return;
      }

      await Promise.all(uploadPromises);
      message.success({ content: translate('clipboardUpload.uploadSuccess'), key: UPLOAD_KEY, duration: 2 });

      if (onUploadSuccess) {
        onUploadSuccess();
      }

    } catch (error: any) {
      console.error('Failed to read clipboard:', error);
      if (error.name === 'NotAllowedError') {
        message.error({ content: translate('clipboardUpload.permissionDenied'), key: UPLOAD_KEY, duration: 4 });
      } else {
        message.error({ content: translate('clipboardUpload.readFailed'), key: UPLOAD_KEY, duration: 3 });
      }
    } finally {
      setUploading(false);
    }
  }, [currentDir, onUploadSuccess, uploading]);

  const clipboardLabel = t('upload.pasteUpload');

  const clipboardAction = useMemo(() => {
    if (!isMobile) {
      return null;
    }

    return {
      key: 'clipboard-upload',
      order: 20,
      icon: uploading ? <SyncOutlined spin /> : <CopyOutlined />,
      label: clipboardLabel,
      onClick: handleClipboardUpload,
      testId: 'clipboard-upload-entry',
    };
  }, [clipboardLabel, handleClipboardUpload, isMobile, uploading]);

  useFloatingAction(clipboardAction);

  return null;
};

export default ClipboardUploadButton;
